export const SIGN_IN_ATTEMPT_WINDOW_MS = 2 * 60_000;

export type GateInput = {
  slug: string;
  status: "pending" | "error" | "success";
  httpStatus: number | null;
  sessionSlug: string | null;
  loginError: string | null;
  signedOut: boolean;
  lastAttemptAt: number | null;
  now: number;
  redirectSuppressed: boolean;
};

export type GateView = "loading" | "render" | "no-access-here" | "unavailable" | "redirect" | "retry" | "error";

export type RetryReason = "sso" | "unavailable" | "signed-out" | "incomplete";

const recentAttempt = (input: GateInput): boolean => input.lastAttemptAt !== null && input.now - input.lastAttemptAt < SIGN_IN_ATTEMPT_WINDOW_MS;

// A 401 normally starts sign-in automatically. Query params and a recent attempt stop that loop and show a card instead.
export const decideGate = (input: GateInput): GateView => {
  if (input.status === "pending") return "loading";
  if (input.status === "success") return input.sessionSlug === input.slug ? "render" : "no-access-here";
  if (input.httpStatus === 403) return "unavailable";
  if (input.httpStatus !== 401) return "error";
  const blocked = input.loginError !== null || input.signedOut || recentAttempt(input) || input.redirectSuppressed;
  return blocked ? "retry" : "redirect";
};

export const retryReason = (input: Pick<GateInput, "loginError" | "signedOut">): RetryReason => {
  if (input.loginError === "sso") return "sso";
  if (input.loginError !== null) return "unavailable";
  return input.signedOut ? "signed-out" : "incomplete";
};

export const RETRY_COPY: Record<RetryReason, { title: string; body: string }> = {
  sso: { title: "Sign-in did not complete", body: "We could not sign you in with your organisation’s account. Try again, or contact your administrator if this keeps happening." },
  unavailable: { title: "Sign-in is unavailable", body: "Sign-in is not available for this portal right now. Try again later or contact your administrator." },
  "signed-out": { title: "You have signed out", body: "Sign in again to continue." },
  incomplete: { title: "Sign-in did not complete", body: "Sign in again to continue." },
};

const attemptKey = (slug: string): string => `portal.signin.attempt:${slug}`;

export const readSignInAttempt = (slug: string, storage: Pick<Storage, "getItem"> = sessionStorage): number | null => {
  try {
    const value = Number(storage.getItem(attemptKey(slug)));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

export const markSignInAttempt = (slug: string, now: number, storage: Pick<Storage, "setItem"> = sessionStorage): void => {
  try {
    storage.setItem(attemptKey(slug), String(now));
  } catch {
    // Storage can be unavailable (private browsing); without the marker the sign-in loop guard relies on the URL params only.
  }
};

export const clearSignInAttempt = (slug: string, storage: Pick<Storage, "removeItem"> = sessionStorage): void => {
  try {
    storage.removeItem(attemptKey(slug));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
};
